/**
 * «Поливы по файлу ирригации» — карточка хозяйства (фермер, менеджер, хоким).
 *
 * Факт полива приходит ТОЛЬКО из файла ирригации: кнопки «отчитаться» у полива
 * нет. Фермер может ОСПОРИТЬ — «полива не было» или «полито, а в файле нет»:
 * спор уходит менеджеру и ничего не меняет в факте. Пока окно идёт, отсутствие
 * события — «ждём подачи», а не «не полито»; после закрытия окна — «без подачи».
 * Рядом с числами — дата последнего опубликованного файла.
 *
 * Все числа — поля сервера (`/farms/{id}/water/irrigations`) и слой метрик
 * `water.*`; браузер ничего не складывает.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Droplets, FileSearch, MessageSquareWarning } from 'lucide-react';
import { api, type FarmIrrigation, type FarmIrrigations as FarmIrrigationsReply } from '../../../api/client';
import { useRequest } from '../../../api/useRequest';
import { useAnalysisContext } from '../../../analysis/context';
import { Badge } from '../../ui/Badge';
import { Card, Grid, Stack } from '../../ui/Card';
import { DataTable, type Column } from '../../ui/DataTable';
import { Disclosure } from '../../ui/Disclosure';
import { EmptyState } from '../../ui/EmptyState';
import { Icon } from '../../ui/Icon';
import { ChoiceSelect } from '../../ui/AnalysisControls';
import { RadioGroup, TextArea } from '../../ui/forms';
import { DetailDrawer, DrawerFormActions } from '../../ui/DetailDrawer';
import { COMMON } from '../../../i18n/common';
import { cycleChoices, pickCycle } from '../../data-source/api/water';
import { KpiCard } from '../../ui/KpiCard';
import { LoadError } from '../../ui/LoadError';
import { SkeletonCard } from '../../ui';
import { countOf, dateShort, haUnit, m3, num, windowLabel, WORDS } from '../../lib/format';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { tr } from '../../../i18n/runtime';
import {
  DISPUTE_REASONS,
  STATE_NOTES,
  STATE_ORDER,
  STATE_TONES,
  STATE_WORDS,
  isState,
  metricById,
  metricText,
  sourceText,
  type IrrigationState,
} from './waterFile';
import './water-file.css';

const m = defineMessages({
  stateCount: { id: 'water.irrigations.stateCount', defaultMessage: '{state} {count}', description: 'Бейдж-счёт в сводке группы поливов: слово состояния и число контуров («без подачи 2»); {state} — слово состояния, {count} — число' },
  disputeGroup: { id: 'water.irrigations.dispute.group', defaultMessage: 'Оспорить полив контура {contour}', description: 'aria-label формы спора фермера о поливе; {contour} — номер контура' },
  disputePlaceholder: { id: 'water.irrigations.dispute.placeholder', defaultMessage: 'Что именно не так — одним-двумя предложениями', description: 'Подсказка в поле пояснения к спору о поливе (фермер)' },
  disputeNoteLabel: { id: 'water.irrigations.dispute.noteLabel', defaultMessage: 'Пояснение к спору', description: 'aria-label поля пояснения к спору о поливе' },
  disputeSend: { id: 'water.irrigations.dispute.send', defaultMessage: 'Отправить менеджеру', description: 'Кнопка формы спора (фермер): отправить спор менеджеру; коротко' },
  disputeNote: { id: 'water.irrigations.dispute.note', defaultMessage: 'Спор не меняет факт: полив отмечает только файл ирригации.', description: 'Пояснение рядом с кнопкой отправки спора: спор не меняет факт полива' },
  disputeSent: { id: 'water.irrigations.dispute.sent', defaultMessage: 'спор отправлен менеджеру', description: 'Статус в строке полива: спор фермера отправлен менеджеру и ждёт разбора; строчными, коротко' },
  disputeResolved: { id: 'water.irrigations.dispute.resolved', defaultMessage: 'спор разобран: {note}', description: 'Статус в строке полива: менеджер разобрал спор; {note} — его пояснение к решению' },
  disputeOpen: { id: 'water.irrigations.dispute.open', defaultMessage: 'Оспорить', description: 'Кнопка в строке полива (фермер): открыть форму спора «полива не было» или «полито, а в файле нет»; коротко' },
  colContour: { id: 'water.irrigations.col.contour', defaultMessage: 'Контур', description: 'Заголовок колонки таблицы поливов: контур' },
  contourNo: { id: 'water.irrigations.contourNo', defaultMessage: '№ {no}', description: 'Номер контура-ссылки в таблице поливов; {no} — номер из кадастра' },
  areaUnknown: { id: 'water.irrigations.areaUnknown', defaultMessage: 'площадь не названа', description: 'Мелкая подпись контура, когда площадь не названа источником' },
  colState: { id: 'water.irrigations.col.state', defaultMessage: 'Состояние', description: 'Заголовок колонки таблицы поливов: состояние полива и окно' },
  colFact: { id: 'water.irrigations.col.fact', defaultMessage: 'Полито', description: 'Заголовок колонки таблицы поливов: факт полива — дата и объём' },
  waitingFile: { id: 'water.irrigations.waitingFile', defaultMessage: 'ждём файл ирригации', description: 'Ячейка факта полива, пока окно идёт и события в файле ещё нет' },
  factDate: { id: 'water.irrigations.factDate', defaultMessage: '{date}, {volume}', description: 'Факт полива в ячейке: дата и объём; {date} — дата или прочерк, {volume} — «1 200 м³» или «объём не указан»' },
  volumeUnknown: { id: 'water.irrigations.volumeUnknown', defaultMessage: 'объём не указан', description: 'Вместо объёма воды, когда источник его не назвал' },
  canal: { id: 'water.irrigations.canal', defaultMessage: 'канал {name}', description: 'Мелкая подпись факта полива: канал; {name} — имя канала из файла' },
  moreEvents: { id: 'water.irrigations.moreEvents', defaultMessage: 'ещё {count}', description: 'Мелкая подпись факта полива: сколько ещё событий полива у этого контура; {count} — число' },
  hintMismatch: { id: 'water.irrigations.hintMismatch', defaultMessage: 'номер полива в файле другой', description: 'Мелкая пометка факта полива: в файле ирригации у события другой номер полива, чем в плане' },
  colDispute: { id: 'water.irrigations.col.dispute', defaultMessage: 'Спор', description: 'Заголовок колонки таблицы поливов: спор фермера' },
  cycleChoice: { id: 'water.irrigations.cycleChoice', defaultMessage: 'Агроцикл', description: 'Подпись выбора агроцикла над карточкой поливов хозяйства' },
  loadWhat: { id: 'water.irrigations.loadWhat', defaultMessage: 'поливы по файлу ирригации', description: 'Что не загрузилось, в винительном падеже, для сообщения «Не удалось загрузить …»' },
  title: { id: 'water.irrigations.title', defaultMessage: 'Поливы по файлу ирригации', description: 'Заголовок карточки хозяйства (фермер, менеджер, хоким): поливы контуров хозяйства по файлу ирригации' },
  loading: { id: 'water.irrigations.loading', defaultMessage: 'Читаем файл ирригации…', description: 'Подпись в скелетоне карточки, пока файл ирригации читается' },
  info: {
    id: 'water.irrigations.info',
    defaultMessage:
      'Факт полива приходит только из файла ирригации: фермер его не отмечает, но может оспорить. Пока окно идёт, отсутствие строки в файле — «ждём подачи», а не «не полито»; после закрытия окна — «без подачи».',
    description: 'Текст всплывающего пояснения карточки поливов хозяйства; «ждём подачи» и «без подачи» — состояния полива (те же слова, что на бейджах)',
  },
  kpiDoneLand: { id: 'water.irrigations.kpi.doneLand', defaultMessage: 'Полито по земле', description: 'Подпись KPI: доля политой земли хозяйства в % площади' },
  kpiDonePairs: { id: 'water.irrigations.kpi.donePairsNote', defaultMessage: 'по парам «контур × полив»: {value}', description: 'Мелкая подпись под KPI «Полито по земле»: та же доля по числу пар «контур × полив»; {value} — процент или «нет данных»' },
  noData: { id: 'water.irrigations.noData', defaultMessage: 'нет данных', description: 'Значение вместо числа, когда сервер его не назвал: не ноль' },
  kpiDelivered: { id: 'water.irrigations.kpi.delivered', defaultMessage: 'Подано по файлу', description: 'Подпись KPI: объём воды (м³), поданной хозяйству по файлу ирригации' },
  kpiDeliveredNote: { id: 'water.irrigations.kpi.deliveredNote', defaultMessage: 'политая земля: {value}', description: 'Мелкая подпись под KPI «Подано по файлу»; {value} — га политой земли или «нет данных»' },
  kpiWaiting: { id: 'water.irrigations.kpi.waiting', defaultMessage: 'Ждём подачи', description: 'Подпись KPI: земля поливов, у которых окно ещё идёт и события в файле нет' },
  kpiWaitingNote: { id: 'water.irrigations.kpi.waitingNote', defaultMessage: 'земля поливов, чьё окно идёт', description: 'Мелкая подпись под KPI «Ждём подачи»' },
  kpiNoDelivery: { id: 'water.irrigations.kpi.noDelivery', defaultMessage: 'Без подачи', description: 'Подпись KPI: число пар «контур × полив», у которых окно закрылось, а события нет' },
  kpiNoDeliveryNote: { id: 'water.irrigations.kpi.noDeliveryNote', defaultMessage: 'окно закрылось, события нет', description: 'Мелкая подпись под KPI «Без подачи»' },
  noIrrigationsTitle: { id: 'water.irrigations.empty.title', defaultMessage: 'Поливов плана в агроцикле нет', description: 'Заголовок пустого состояния карточки поливов хозяйства' },
  noIrrigationsReason: { id: 'water.irrigations.empty.reason', defaultMessage: 'у хозяйства нет запущенных поливов этого агроцикла в срезе', description: 'Пояснение пустого состояния; строчными, после заголовка' },
  groupSummary: { id: 'water.irrigations.groupSummary', defaultMessage: '{window} · {contours}', description: 'Сводка свёрнутой группы поливов: окно полива и число контуров; {window} — «01–06 окт», {contours} — «12 контуров»' },
  contoursDenominator: {
    id: 'water.irrigations.denominator.contours',
    defaultMessage: '{count, plural, one {контур} few {контура} many {контуров} other {контура}}',
    description: 'Единица знаменателя под таблицей поливов («показано 8 из 20 контуров»); только слово, без числа',
  },
  noContoursTitle: { id: 'water.irrigations.noContours.title', defaultMessage: 'Контуров нет', description: 'Заголовок пустого состояния таблицы группы поливов' },
  noContoursReason: { id: 'water.irrigations.noContours.reason', defaultMessage: 'в этом поливе у хозяйства нет контуров', description: 'Пояснение пустого состояния таблицы группы поливов; строчными, после заголовка' },
  extraLabel: { id: 'water.irrigations.extra.label', defaultMessage: 'Поливы вне плана и вне агроцикла', description: 'Заголовок раскрывающегося блока: поливы, которым нет плана или агроцикла' },
  extraSummary: { id: 'water.irrigations.extra.summary', defaultMessage: 'вне плана {unplanned} · вне агроцикла {outside}', description: 'Сводка свёрнутого блока; {unplanned}/{outside} — числа событий полива' },
  extraNote: {
    id: 'water.irrigations.extra.note',
    defaultMessage:
      'В знаменатели плана, «после срока» и «поливов без подачи» эти события не входят: агроцикл на земле есть, а плана полива под них нет (или агроцикла нет вовсе) — вода видна отдельным слоем.',
    description: 'Пояснение в раскрытом блоке «Поливы вне плана и вне агроцикла»',
  },
  kindUnplanned: { id: 'water.irrigations.extra.kindUnplanned', defaultMessage: 'вне плана', description: 'Бейдж события полива: агроцикл на земле есть, плана полива нет; строчными, коротко' },
  kindOutside: { id: 'water.irrigations.extra.kindOutside', defaultMessage: 'вне агроцикла', description: 'Бейдж события полива: агроцикла на земле нет; строчными, коротко' },
  extraEvent: { id: 'water.irrigations.extra.event', defaultMessage: 'контур № {no} · {date}, {volume}', description: 'Строка события полива вне плана/агроцикла; {no} — номер контура, {date} — дата, {volume} — «1 200 м³» или «объём не указан»' },
});

/** Состояния группы словами и числами сервера: «без подачи 2 · ждём 1 · полито 3». */
function CountChips({ counts }: { counts: Record<string, number> }) {
  const t = useT();
  return (
    <span className="wf-chips">
      {STATE_ORDER.filter((s) => (counts[s] ?? 0) > 0).map((s) => (
        <Badge key={s} tone={STATE_TONES[s]}>
          {t(m.stateCount, { state: STATE_WORDS[s], count: num(counts[s] ?? 0) })}
        </Badge>
      ))}
    </span>
  );
}

/**
 * Форма спора открывается в боковом ящике, а не в ячейке колонки «Спор»: в
 * ячейке телефона (390 px) форма сжималась в полоску. В ящике она занимает всю
 * ширину и на телефоне, и на компьютере; несохранённый ввод спрашивает
 * «Закрыть без сохранения?».
 */
function DisputeDrawer({ row, onDone, onClose }: { row: FarmIrrigation; onDone: () => void; onClose: () => void }) {
  const t = useT();
  const { viewParams } = useAnalysisContext();
  const [reason, setReason] = useState<string>(DISPUTE_REASONS[0].id);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const event = row.events[0] ?? null;

  const submit = async () => {
    const prefix = DISPUTE_REASONS.find((r) => r.id === reason)?.prefix ?? '';
    setBusy(true);
    setError(null);
    try {
      await api.raiseWaterDispute(
        { obligation_id: row.obligation_id, event_id: event?.event_id ?? null, note: text.trim() ? `${prefix}: ${text.trim()}` : prefix },
        viewParams,
      );
      onDone();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      setBusy(false);
    }
  };

  const title = t(m.disputeGroup, { contour: row.contour_no });
  return (
    <DetailDrawer title={title} onClose={onClose} isDirty={text.trim() !== '' || reason !== DISPUTE_REASONS[0]?.id}>
      <div className="wf-dispute" role="group" aria-label={title}>
        <RadioGroup
          aria-label={title}
          value={reason}
          onChange={setReason}
          options={DISPUTE_REASONS.map((r) => ({ id: r.id, name: r.label }))}
        />
        <TextArea
          minRows={3}
          value={text}
          placeholder={t(m.disputePlaceholder)}
          onChange={setText}
          aria-label={t(m.disputeNoteLabel)}
        />
        {error ? (
          <p className="wf-error" role="alert">
            {error}
          </p>
        ) : null}
        <DrawerFormActions
          note={t(m.disputeNote)}
          primary={{ label: t(m.disputeSend), isPending: busy, onPress: () => void submit() }}
          cancel={t(COMMON.cancel)}
        />
      </div>
    </DetailDrawer>
  );
}

function RowActions({ row, onChanged }: { row: FarmIrrigation; onChanged: () => void }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const openDispute = row.disputes.find((d) => d.status === 'open') ?? null;
  const last = row.disputes[0] ?? null;
  return (
    <div className="wf-actions">
      {openDispute ? (
        <span className="wf-dispute-state" title={openDispute.note}>
          <Icon icon={MessageSquareWarning} size={16} /> {t(m.disputeSent)}
        </span>
      ) : (
        <>
          {last && last.status === 'resolved' ? (
            <span className="t-caption" title={last.resolution_note}>
              {t(m.disputeResolved, { note: last.resolution_note })}
            </span>
          ) : null}
          {row.can_dispute ? (
            <button type="button" className="btn btn--secondary btn--sm focus-ring" aria-haspopup="dialog" onClick={() => setOpen(true)}>
              {t(m.disputeOpen)}
            </button>
          ) : null}
        </>
      )}
      {open && !openDispute ? (
        <DisputeDrawer
          row={row}
          onClose={() => setOpen(false)}
          onDone={() => {
            setOpen(false);
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

function rowColumns(onChanged: () => void): Column<FarmIrrigation>[] {
  return [
    {
      key: 'contour',
      header: tr(m.colContour),
      render: (r) => (
        <span className="wcell">
          <Link to={`/contours/${r.contour_id}`}>{tr(m.contourNo, { no: r.contour_no })}</Link>
          <span className="wcell__sub">{r.area_ha === null ? tr(m.areaUnknown) : haUnit(Number(r.area_ha))}</span>
        </span>
      ),
    },
    {
      key: 'state',
      header: tr(m.colState),
      render: (r) => {
        const state: IrrigationState = isState(r.state) ? r.state : 'upcoming';
        return (
          <span className="wcell" title={STATE_NOTES[state]}>
            <Badge tone={STATE_TONES[state]}>{STATE_WORDS[state]}</Badge>
            <span className="wcell__sub">{windowLabel(r.window_from, r.window_to)}</span>
          </span>
        );
      },
    },
    {
      key: 'fact',
      header: tr(m.colFact),
      render: (r) =>
        r.events.length === 0 ? (
          <span className="t-caption">{r.state === 'waiting' ? tr(m.waitingFile) : '—'}</span>
        ) : (
          <span className="wcell">
            <span>
              {tr(m.factDate, {
                date: r.done_on ? dateShort(r.done_on) : '—',
                volume: r.volume_m3 === null ? tr(m.volumeUnknown) : m3(Number(r.volume_m3)),
              })}
            </span>
            <span className="wcell__sub">
              {[
                r.events[0]!.canal ? tr(m.canal, { name: r.events[0]!.canal }) : null,
                sourceText(r.events[0]!.source),
                r.events.length > 1 ? tr(m.moreEvents, { count: num(r.events.length - 1) }) : null,
                r.events.some((e) => e.hint_mismatch) ? tr(m.hintMismatch) : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </span>
        ),
    },
    { key: 'act', header: tr(m.colDispute), render: (r) => <RowActions row={r} onChanged={onChanged} /> },
  ];
}

export function FarmIrrigations({ actorId, cycle }: { actorId: number; cycle?: string | null }) {
  const t = useT();
  const { viewParams } = useAnalysisContext();
  const key = JSON.stringify(viewParams);
  // Цикл не назван — выбор активного цикла хозяйства, как на экране «Вода».
  const cycles = useRequest(() => (cycle ? Promise.resolve(null) : api.seasonCycles(viewParams)), [key, cycle ?? '']);
  const [picked, setPicked] = useState<string | null>(null);
  const choices = useMemo(() => (cycles.data ? cycleChoices(cycles.data) : []), [cycles.data]);
  // Дата среза — названная контекстом либо «сегодня» сервера из ответа (`horizon`), не часы браузера.
  const ref = typeof viewParams.as_of === 'string' && viewParams.as_of ? viewParams.as_of : (cycles.data?.horizon ?? null);
  const chosen = cycle ?? pickCycle(choices, picked, ref)?.cycle ?? null;
  const res = useRequest(
    () => (cycle || chosen || cycles.state === 'ready' ? api.farmIrrigations(actorId, { ...viewParams, ...(chosen ? { cycle: chosen } : {}) }) : Promise.resolve(null)),
    [key, actorId, chosen ?? '', cycles.state],
  );
  if (res.data === null) {
    if (res.state === 'error') return <LoadError compact what={t(m.loadWhat)} error={res.error} onRetry={res.reload} />;
    return <SkeletonCard quiet title={t(m.title)} size="lg"><Stack tight><span className="t-caption">{t(m.loading)}</span></Stack></SkeletonCard>;
  }
  return (
    <Body
      data={res.data}
      reload={res.reload}
      picker={
        !cycle && choices.length > 1 ? (
          <ChoiceSelect label={t(m.cycleChoice)} value={chosen ?? ''} options={choices.map((c) => ({ id: c.cycle, name: c.name }))} onChange={setPicked} />
        ) : null
      }
    />
  );
}

function Body({ data, reload, picker }: { data: FarmIrrigationsReply; reload: () => void; picker: ReactNode }) {
  const t = useT();
  const byCampaign = useMemo(() => {
    const map = new Map<number, FarmIrrigation[]>();
    for (const row of data.irrigations) {
      const list = map.get(row.campaign_id) ?? [];
      list.push(row);
      map.set(row.campaign_id, list);
    }
    return map;
  }, [data.irrigations]);
  const columns = useMemo(() => rowColumns(reload), [reload]);
  const delivered = metricById(data.metrics, 'water.delivered_m3');
  const land = metricById(data.metrics, 'water.irrigated_land_ha');
  const doneLand = metricById(data.metrics, 'water.irrigation_done_land_pct');
  const donePairs = metricById(data.metrics, 'water.irrigation_done_pairs_pct');
  const noDelivery = metricById(data.metrics, 'water.no_delivery_pairs');
  const waitingLand = metricById(data.metrics, 'water.irrigation_waiting_land_ha');

  return (
    <div data-guide="farm-irrigations">
    <Card
      size="lg"
      title={t(m.title)}
      info={t(m.info)}
      aside={<Badge tone="outline">{data.freshness.note}</Badge>}
    >
      <Stack>
        {picker ? <div className="chips">{picker}</div> : null}
        <Grid cols={4}>
          <KpiCard
            label={t(m.kpiDoneLand)}
            icon={Droplets}
            value={doneLand ? metricText(doneLand) : '…'}
            note={t(m.kpiDonePairs, { value: donePairs ? metricText(donePairs) : t(m.noData) })}
          />
          <KpiCard
            label={t(m.kpiDelivered)}
            icon={Droplets}
            value={delivered ? metricText(delivered) : '…'}
            note={t(m.kpiDeliveredNote, { value: land ? metricText(land) : t(m.noData) })}
          />
          <KpiCard
            label={t(m.kpiWaiting)}
            icon={FileSearch}
            value={waitingLand ? metricText(waitingLand) : '…'}
            note={t(m.kpiWaitingNote)}
          />
          <KpiCard
            label={t(m.kpiNoDelivery)}
            icon={FileSearch}
            tone={(noDelivery?.value ?? 0) > 0 ? 'warning' : undefined}
            value={noDelivery ? metricText(noDelivery) : '…'}
            note={t(m.kpiNoDeliveryNote)}
          />
        </Grid>

        {data.groups.length === 0 ? (
          <EmptyState compact title={t(m.noIrrigationsTitle)} reason={t(m.noIrrigationsReason)} />
        ) : (
          data.groups.map((g, index) => (
            <Disclosure
              key={g.campaign_id}
              label={g.name.includes(':') ? g.name.slice(g.name.indexOf(':') + 1).trim() : g.name}
              defaultOpen={index === 0 && (g.counts.no_delivery ?? 0) + (g.counts.waiting ?? 0) > 0}
              summary={
                <span className="wf-summary">
                  <span>{t(m.groupSummary, { window: windowLabel(g.window_from, g.window_to), contours: countOf(g.pairs, WORDS.contour) })}</span>
                  <CountChips counts={g.counts} />
                </span>
              }
            >
              <DataTable
                columns={columns}
                rows={byCampaign.get(g.campaign_id) ?? []}
                getKey={(r) => String(r.obligation_id)}
                denominator={m.contoursDenominator}
                limit={8}
                empty={{ title: t(m.noContoursTitle), reason: t(m.noContoursReason) }}
              />
            </Disclosure>
          ))
        )}

        {data.unplanned.length > 0 || data.outside_cycle.length > 0 ? (
          <Disclosure
            label={t(m.extraLabel)}
            summary={t(m.extraSummary, { unplanned: num(data.unplanned.length), outside: num(data.outside_cycle.length) })}
          >
            <Stack tight>
              <p className="t-caption">{t(m.extraNote)}</p>
              {[...data.unplanned.map((e) => ({ ...e, kind: t(m.kindUnplanned) })), ...data.outside_cycle.map((e) => ({ ...e, kind: t(m.kindOutside) }))]
                .slice(0, 30)
                .map((e) => (
                  <div key={`${e.kind}-${e.event_id}`} className="wf-extra">
                    <Badge tone="outline">{e.kind}</Badge>
                    <span>
                      {t(m.extraEvent, {
                        no: e.contour_no,
                        date: dateShort(e.date_from),
                        volume: e.volume_m3 === null ? t(m.volumeUnknown) : m3(Number(e.volume_m3)),
                      })}
                    </span>
                    <span className="t-caption">{sourceText(e.source)}</span>
                  </div>
                ))}
            </Stack>
          </Disclosure>
        ) : null}
        <p className="t-caption">{data.note}</p>
      </Stack>
    </Card>
    </div>
  );
}
